import { octree, type Octree } from "d3-octree";
import { EigenvalueDecomposition, Matrix } from "ml-matrix";
import { Vector3 } from "three";
import { CYLINDER_FIT_CONFIG } from "./cylinder-fit";

/** Immutable, bounded local point index with lazily cached position-derived normals. */
export class PipeFitNeighborhood {
  private readonly tree: Octree<Vector3>;
  private readonly normals = new Map<Vector3, Vector3 | undefined>();

  /** @param points Owned immutable points from a loaded region, not the entire source cloud. */
  constructor(readonly points: Vector3[]) {
    this.tree = octree(
      points,
      (point) => point.x,
      (point) => point.y,
      (point) => point.z,
    );
  }

  /**
   * @param point Query point; the identical stored point is excluded.
   * @param count Maximum neighbors.
   * @param radius Optional spatial bound.
   * @returns Exact nearest stored points, ordered by squared distance.
   */
  nearest(
    point: Vector3,
    count: number,
    radius = Infinity,
  ): Array<{ point: Vector3; distance: number }> {
    const nearest: Array<{ point: Vector3; distance: number }> = [];
    let limit = radius * radius;
    this.tree.visit((node, x0, y0, z0, x1, y1, z1) => {
      const distance =
        Math.max(x0 - point.x, 0, point.x - x1) ** 2 +
        Math.max(y0 - point.y, 0, point.y - y1) ** 2 +
        Math.max(z0 - point.z, 0, point.z - z1) ** 2;
      if (distance > limit) return true;
      if (Array.isArray(node)) return false;
      let leaf: typeof node | undefined = node;
      while (leaf) {
        if (leaf.data !== point) {
          const squared = leaf.data.distanceToSquared(point);
          if (squared <= limit) {
            nearest.push({ point: leaf.data, distance: squared });
            nearest.sort((left, right) => left.distance - right.distance);
            if (nearest.length > count) nearest.pop();
            if (nearest.length === count) limit = nearest[count - 1].distance;
          }
        }
        leaf = leaf.next && !Array.isArray(leaf.next) ? leaf.next : undefined;
      }
      return false;
    });
    return nearest;
  }

  /** @returns Median sixth-neighbor spacing over a bounded deterministic sample. */
  spacing(): number | undefined {
    const distances: number[] = [];
    const stride = Math.max(1, Math.floor(this.points.length / 64));
    for (let index = 0; index < this.points.length; index += stride) {
      const neighbors = this.nearest(this.points[index], 6);
      if (neighbors.length === 6 && neighbors[5].distance > 0) {
        distances.push(Math.sqrt(neighbors[5].distance));
      }
    }
    distances.sort((left, right) => left - right);
    return distances.length
      ? distances[Math.floor(distances.length / 2)]
      : undefined;
  }

  /**
   * @param point Stored sample whose local normal is needed.
   * @returns Cached PCA normal, or undefined for insufficient two-dimensional support.
   */
  normal(point: Vector3): Vector3 | undefined {
    if (this.normals.has(point)) return this.normals.get(point)?.clone();
    const neighbors = this.nearest(point, CYLINDER_FIT_CONFIG.normalNeighbors);
    let normal: Vector3 | undefined;
    if (neighbors.length === CYLINDER_FIT_CONFIG.normalNeighbors) {
      const center = neighbors
        .reduce((sum, neighbor) => sum.add(neighbor.point), new Vector3())
        .divideScalar(neighbors.length);
      const scatter = Matrix.zeros(3, 3);
      for (const neighbor of neighbors) {
        const values = neighbor.point.clone().sub(center).toArray();
        for (let row = 0; row < 3; row++) {
          for (let column = 0; column < 3; column++) {
            scatter.set(
              row,
              column,
              scatter.get(row, column) + values[row] * values[column],
            );
          }
        }
      }
      const decomposition = new EigenvalueDecomposition(scatter, {
        assumeSymmetric: true,
      });
      const values = decomposition.realEigenvalues;
      if (
        values[1] > values[2] * CYLINDER_FIT_CONFIG.minimumNormalSpread &&
        values[0] < values[1] * CYLINDER_FIT_CONFIG.eigenGapRatio
      ) {
        normal = new Vector3()
          .fromArray(decomposition.eigenvectorMatrix.getColumn(0))
          .normalize();
      }
    }
    this.normals.set(point, normal);
    return normal?.clone();
  }
}
